import { InvoiceSheet, PurchaseOrderSheet, RecordPaymentForm, SalesOrderSheet } from "@/components/operations";
import { SuccessActionPanel } from "@/components/operations/SuccessActionPanel";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  formatStatusLabel,
  getPurchaseOrderDisplayStatus,
} from "@/components/operations/operations-shared";
import { getPurchaseOrderTotal } from "@/utils/order-total";
import { formatTimestamp } from "@/utils/formatting";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  exportInvoicesToCsv,
  exportInvoicesToExcel,
  exportPaymentsToCsv,
  exportPaymentsToExcel,
  exportPurchaseOrdersToCsv,
  exportPurchaseOrdersToExcel,
} from "@/utils/csv-export";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarIcon,
  Download,
  Loader2,
  X,
} from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  fetchAllInvoices,
  fetchAllPayments,
  fetchAllPurchaseOrders,
  useAllPayments,
  useCreateInvoice,
  useInvoices,
  usePurchaseOrders,
  useRecordPayment,
  useSalesOrders,
  useUpdatePaymentStatus,
} from "@/hooks";
import type { Invoice, Payment, PurchaseOrder } from "@/core";
import {
  buildDateRangeConstraints,
  buildExportSuffix,
  formatDateRangeLabel,
} from "@/utils";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const FINANCE_PAGE_SIZE = 100;

// "cancelled" is a display-only status: cancelled POs are stored as "voided" and
// split out via getPurchaseOrderDisplayStatus so the two dead-PO cases filter apart.
type POStatus = "open" | "closed" | "blocked" | "voided" | "cancelled";
type PayablesSortKey = "nextPaymentDate" | "outstanding";
interface PayablesSortState { key: PayablesSortKey; direction: "asc" | "desc" }

function PayablesSortButton({
  label,
  sortKey,
  sortState,
  onSort,
}: {
  label: string;
  sortKey: PayablesSortKey;
  sortState: PayablesSortState;
  onSort: (key: PayablesSortKey) => void;
}) {
  const isActive = sortState.key === sortKey;
  return (
    <Button
      variant="ghost"
      onClick={() => onSort(sortKey)}
      className="h-auto p-0 font-medium hover:bg-transparent"
    >
      {label}
      {isActive ? (
        sortState.direction === "asc" ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3" />
      )}
    </Button>
  );
}

export default function FinancePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "payables" ? "payables" : "receivables";
  const selectedInvoiceId = searchParams.get("invoiceId") || "";
  const selectedSoId = searchParams.get("soId") || "";
  const selectedPoId = searchParams.get("poId") || "";
  const activeSheetType = selectedInvoiceId
    ? "invoice"
    : selectedSoId
      ? "so"
      : selectedPoId
        ? "po"
        : null;

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const invoiceRangeConstraints = useMemo(
    () => buildDateRangeConstraints<Invoice>(dateRange, "issuedAt"),
    [dateRange],
  );
  const purchaseOrderRangeConstraints = useMemo(
    () => buildDateRangeConstraints<PurchaseOrder>(dateRange, "createdAt"),
    [dateRange],
  );

  const invoicesQuery = useInvoices({
    queryConstraints: invoiceRangeConstraints,
    pagination: { limit: FINANCE_PAGE_SIZE },
    orderBy: { field: "issuedAt", direction: "desc" },
  });
  // Deliberately NOT date-filtered: the cleared totals below are derived from
  // every payment ever recorded, so windowing this would under-report what has
  // been paid on invoices and POs that fall inside the window.
  const { payments } = useAllPayments();
  const salesOrdersQuery = useSalesOrders({
    pagination: { limit: 100 },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const purchaseOrdersQuery = usePurchaseOrders({
    queryConstraints: purchaseOrderRangeConstraints,
    pagination: { limit: FINANCE_PAGE_SIZE },
    orderBy: { field: "createdAt", direction: "desc" },
  });

  const createInvoiceMutation = useCreateInvoice();
  const recordPaymentMutation = useRecordPayment();
  const markClearedMutation = useUpdatePaymentStatus();
  const [soId, setSoId] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [invoicePaymentId, setInvoicePaymentId] = useState("");
  const [invoicePaymentAmount, setInvoicePaymentAmount] = useState("");
  const recordPoId = searchParams.get("recordPoId") || "";
  const [poPaymentId, setPoPaymentId] = useState(recordPoId);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const [selectedPayablePurchaseOrderIds, setSelectedPayablePurchaseOrderIds] = useState<
    Set<string>
  >(new Set());
  const [poStatusFilter, setPoStatusFilter] = useState<POStatus | "all">("all");
  const [payablesSortState, setPayablesSortState] = useState<PayablesSortState>({
    key: "nextPaymentDate",
    direction: "asc",
  });
  const [successState, setSuccessState] = useState<{
    title: string;
    description: string;
    details?: string[];
    nextAction?: string;
    actions?: Array<{ label: string; href: string }>;
  } | null>(null);

  const invoices = useMemo(() => invoicesQuery.data?.pages.flat() ?? [], [invoicesQuery.data]);
  const salesOrders = useMemo(() => salesOrdersQuery.data?.pages.flat() ?? [], [salesOrdersQuery.data]);
  const purchaseOrders = useMemo(
    () => purchaseOrdersQuery.data?.pages.flat() ?? [],
    [purchaseOrdersQuery.data],
  );
  const selectedInvoices = useMemo(
    () => invoices.filter((invoice) => selectedInvoiceIds.has(invoice.id)),
    [invoices, selectedInvoiceIds],
  );
  const selectedPayments = useMemo(
    () =>
      payments.filter(
        (payment) =>
          payment.appliesToType === "po" &&
          selectedPayablePurchaseOrderIds.has(payment.appliesToId),
      ),
    [payments, selectedPayablePurchaseOrderIds],
  );
  const allInvoicesSelected =
    invoices.length > 0 &&
    invoices.every((invoice) => selectedInvoiceIds.has(invoice.id));
  const someInvoicesSelected =
    !allInvoicesSelected &&
    invoices.some((invoice) => selectedInvoiceIds.has(invoice.id));
  const clearedByInvoiceId = useMemo(() => {
    const map = new Map<string, number>();
    payments
      .filter((payment) => payment.appliesToType === "invoice" && payment.status === "cleared" && payment.direction !== "incoming" && !payment.source)
      .forEach((payment) => {
        map.set(payment.appliesToId, (map.get(payment.appliesToId) || 0) + payment.amount);
      });
    return map;
  }, [payments]);

  const clearedByPoId = useMemo(() => {
    const map = new Map<string, number>();
    payments
      .filter((payment) => payment.appliesToType === "po" && payment.status === "cleared" && payment.direction !== "incoming" && !payment.source)
      .forEach((payment) => {
        map.set(payment.appliesToId, (map.get(payment.appliesToId) || 0) + payment.amount);
      });
    return map;
  }, [payments]);

  const filteredSortedPayables = useMemo(() => {
    const filtered = poStatusFilter === "all"
      ? purchaseOrders
      : purchaseOrders.filter(
          (po) => getPurchaseOrderDisplayStatus(po) === poStatusFilter,
        );

    const directionMultiplier = payablesSortState.direction === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      if (payablesSortState.key === "nextPaymentDate") {
        const aTime = a.nextPaymentDate?.getTime() ?? 0;
        const bTime = b.nextPaymentDate?.getTime() ?? 0;
        return (aTime - bTime) * directionMultiplier;
      }
      const aTotal = getPurchaseOrderTotal({ buyPrice: a.buyPrice, taxCost: a.taxCost, totalAmount: a.totalAmount });
      const bTotal = getPurchaseOrderTotal({ buyPrice: b.buyPrice, taxCost: b.taxCost, totalAmount: b.totalAmount });
      const aOutstanding = Math.max(0, aTotal - (clearedByPoId.get(a.id) || 0));
      const bOutstanding = Math.max(0, bTotal - (clearedByPoId.get(b.id) || 0));
      return (aOutstanding - bOutstanding) * directionMultiplier;
    });
  }, [purchaseOrders, poStatusFilter, payablesSortState, clearedByPoId]);

  const allPayablesSelected =
    filteredSortedPayables.length > 0 &&
    filteredSortedPayables.every((purchaseOrder) =>
      selectedPayablePurchaseOrderIds.has(purchaseOrder.id),
    );
  const somePayablesSelected =
    !allPayablesSelected &&
    filteredSortedPayables.some((purchaseOrder) =>
      selectedPayablePurchaseOrderIds.has(purchaseOrder.id),
    );

  const scheduledPayments = useMemo(
    () =>
      payments
        .filter((p) => p.appliesToType === "po" && p.status === "pending")
        .sort((a, b) => {
          const aTime = new Date(a.paidAt || a.createdAt).getTime();
          const bTime = new Date(b.paidAt || b.createdAt).getTime();
          return aTime - bTime;
        }),
    [payments],
  );

  const [exportPending, setExportPending] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  /**
   * Exports the FULL set matching the selected date range, fetched directly
   * rather than from the paginated tables.
   */
  const runExport = async (
    kind: "invoices" | "payments" | "payables",
    fileFormat: "csv" | "excel",
  ) => {
    setExportPending(true);
    setExportMessage(null);

    try {
      const suffix = buildExportSuffix(dateRange);
      let count = 0;

      if (kind === "invoices") {
        const rows = await fetchAllInvoices(invoiceRangeConstraints);
        count = rows.length;
        if (count) {
          if (fileFormat === "csv") {
            exportInvoicesToCsv(rows, `invoices-${suffix}.csv`);
          } else {
            exportInvoicesToExcel(rows, `invoices-${suffix}.xlsx`);
          }
        }
      } else if (kind === "payments") {
        // Payments carry no issuedAt — window them by createdAt instead.
        const rows = await fetchAllPayments(
          buildDateRangeConstraints<Payment>(dateRange, "createdAt"),
        );
        count = rows.length;
        if (count) {
          if (fileFormat === "csv") {
            exportPaymentsToCsv(rows, `payments-${suffix}.csv`);
          } else {
            exportPaymentsToExcel(rows, `payments-${suffix}.xlsx`);
          }
        }
      } else {
        const fetched = await fetchAllPurchaseOrders(purchaseOrderRangeConstraints);
        const rows =
          poStatusFilter === "all"
            ? fetched
            : fetched.filter(
                (po) => getPurchaseOrderDisplayStatus(po) === poStatusFilter,
              );
        count = rows.length;
        if (count) {
          if (fileFormat === "csv") {
            exportPurchaseOrdersToCsv(rows, `payables-${suffix}.csv`);
          } else {
            exportPurchaseOrdersToExcel(rows, `payables-${suffix}.xlsx`);
          }
        }
      }

      setExportMessage(
        count === 0
          ? `No ${kind} found for ${formatDateRangeLabel(dateRange)}.`
          : `Exported ${count.toLocaleString()} ${kind} (${formatDateRangeLabel(dateRange)}).`,
      );
    } catch (error) {
      setExportMessage(
        error instanceof Error
          ? `Export failed: ${error.message}`
          : "Export failed. Please try again.",
      );
    } finally {
      setExportPending(false);
    }
  };

  const handleCreateInvoice = async () => {
    const result = await createInvoiceMutation.mutateAsync({
      soId,
      amountDue: Number(amountDue),
      currency: "USD",
    });

    setSuccessState({
      title: "Invoice created",
      description: "The invoice is linked to the sales order and inventory workspace.",
      actions: [
        { label: "Open invoice", href: `/operations/finance?tab=receivables&invoiceId=${result.invoiceId}` },
      ],
    });
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("poId");
      next.delete("soId");
      next.set("tab", "receivables");
      next.set("invoiceId", result.invoiceId);
      return next;
    }, { replace: true });
  };

  const handleRecordInvoicePayment = async () => {
    await recordPaymentMutation.mutateAsync({
      appliesToType: "invoice",
      appliesToId: invoicePaymentId,
      amount: Number(invoicePaymentAmount),
      method: "paypal",
      status: "cleared",
    });
    setInvoicePaymentAmount("");
  };


  const openFinanceSheet = (payload: {
    type: "invoice" | "so" | "po";
    id: string;
    tab?: "receivables" | "payables";
  }) => {
    const { type, id, tab } = payload;
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("invoiceId");
      next.delete("soId");
      next.delete("poId");
      next.set(type === "invoice" ? "invoiceId" : type === "so" ? "soId" : "poId", id);
      next.set("tab", tab || (type === "po" ? "payables" : "receivables"));
      return next;
    }, { replace: true });
  };

  const closeFinanceSheet = (type: "invoice" | "so" | "po") => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete(type === "invoice" ? "invoiceId" : type === "so" ? "soId" : "poId");
      return next;
    }, { replace: true });
  };

  const toggleInvoiceSelection = (invoiceId: string, checked: boolean) => {
    setSelectedInvoiceIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(invoiceId);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
  };

  const toggleAllInvoiceSelection = (checked: boolean) => {
    setSelectedInvoiceIds((previous) => {
      const next = new Set(previous);

      invoices.forEach((invoice) => {
        if (checked) {
          next.add(invoice.id);
        } else {
          next.delete(invoice.id);
        }
      });

      return next;
    });
  };

  const togglePayableSelection = (purchaseOrderId: string, checked: boolean) => {
    setSelectedPayablePurchaseOrderIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(purchaseOrderId);
      } else {
        next.delete(purchaseOrderId);
      }
      return next;
    });
  };

  const toggleAllPayableSelection = (checked: boolean) => {
    setSelectedPayablePurchaseOrderIds((previous) => {
      const next = new Set(previous);

      filteredSortedPayables.forEach((purchaseOrder) => {
        if (checked) {
          next.add(purchaseOrder.id);
        } else {
          next.delete(purchaseOrder.id);
        }
      });

      return next;
    });
  };

  const handlePayablesSort = (key: PayablesSortKey) => {
    setPayablesSortState((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      {successState ? (
        <SuccessActionPanel
          title={successState.title}
          description={successState.description}
          details={successState.details}
          nextAction={successState.nextAction}
          actions={successState.actions}
          onDismiss={() => setSuccessState(null)}
        />
      ) : null}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setSearchParams({ tab: value })}
        className="space-y-6"
      >
        <Card>
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Financials</CardTitle>
              <CardDescription>
                Receivables and payables remain order-linked, while navigation routes through inventory workspaces.
                The date range filters invoices by issue date and payables by creation date; exports always cover the
                whole range, not just the rows loaded below.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-w-0 justify-start overflow-hidden text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">{formatDateRangeLabel(dateRange)}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
              {dateRange?.from ? (
                <Button variant="ghost" size="sm" onClick={() => setDateRange(undefined)}>
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              ) : null}
              <TabsList>
                <TabsTrigger value="receivables">Receivables</TabsTrigger>
                <TabsTrigger value="payables">Payables</TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>
          {exportMessage ? (
            <CardContent className="pt-0 text-sm text-foreground">
              {exportMessage}
            </CardContent>
          ) : null}
        </Card>

        <TabsContent value="receivables" className="mt-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create Invoice</CardTitle>
              <CardDescription>Create an invoice linked to a sell order.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <Select value={soId} onValueChange={setSoId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sell order" />
                </SelectTrigger>
                <SelectContent>
                  {salesOrders.map((salesOrder) => (
                    <SelectItem key={salesOrder.id} value={salesOrder.id}>
                      {salesOrder.clientDisplayName || salesOrder.clientEmail || salesOrder.id.slice(0, 8)} · {salesOrder.productName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={amountDue} onChange={(event) => setAmountDue(event.target.value)} placeholder="Amount due" />
              <Button
                onClick={handleCreateInvoice}
                disabled={!soId || !amountDue}
                loading={createInvoiceMutation.isPending}
                loadingText="Creating..."
              >
                Create Invoice
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Invoices</CardTitle>
                <CardDescription>Open invoice details, then continue in the linked inventory workspace.</CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={exportPending}>
                    {exportPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {exportPending ? "Exporting..." : "Export"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={selectedInvoices.length === 0}
                    onClick={() =>
                      exportInvoicesToCsv(
                        selectedInvoices,
                        `invoices-selected-${buildExportSuffix(dateRange)}.csv`,
                      )
                    }
                  >
                    Export Selected CSV ({selectedInvoices.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={selectedInvoices.length === 0}
                    onClick={() =>
                      exportInvoicesToExcel(
                        selectedInvoices,
                        `invoices-selected-${buildExportSuffix(dateRange)}.xlsx`,
                      )
                    }
                  >
                    Export Selected Excel ({selectedInvoices.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("invoices", "csv")}>
                    {dateRange?.from
                      ? "Export CSV — all in date range"
                      : "Export CSV — all invoices"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("invoices", "excel")}>
                    {dateRange?.from
                      ? "Export Excel — all in date range"
                      : "Export Excel — all invoices"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent>
              <div className="mb-4 text-sm text-muted-foreground">
                Showing {invoices.length.toLocaleString()} invoice
                {invoices.length === 1 ? "" : "s"}
                {invoicesQuery.hasNextPage ? " (more available)" : ""} ·{" "}
                {formatDateRangeLabel(dateRange)}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label="Select all invoices"
                        checked={
                          allInvoicesSelected
                            ? true
                            : someInvoicesSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={(checked) =>
                          toggleAllInvoiceSelection(checked === true)
                        }
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableHead>
                    <TableHead>Inventory</TableHead>
                    <TableHead>Sales Order</TableHead>
                    <TableHead className="text-right">Amount Due</TableHead>
                    <TableHead className="text-right">Cleared</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const linkedSalesOrder = salesOrders.find((salesOrder) => salesOrder.id === invoice.soId);
                    const cleared = clearedByInvoiceId.get(invoice.id) || 0;
                    return (
                      <TableRow
                        key={invoice.id}
                        className="cursor-pointer"
                        onClick={() =>
                          openFinanceSheet({
                            type: "invoice",
                            id: invoice.id,
                            tab: "receivables",
                          })
                        }
                      >
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            aria-label={`Select invoice ${invoice.id.slice(0, 8)}`}
                            checked={selectedInvoiceIds.has(invoice.id)}
                            onCheckedChange={(checked) =>
                              toggleInvoiceSelection(invoice.id, checked === true)
                            }
                          />
                        </TableCell>
                        <TableCell>{invoice.inventoryId.slice(0, 8)}</TableCell>
                        <TableCell>
                          {linkedSalesOrder?.clientDisplayName ||
                            linkedSalesOrder?.clientEmail ||
                            invoice.soId?.slice(0, 8) ||
                            "-"}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency({ value: invoice.amountDue })}</TableCell>
                        <TableCell className="text-right">{formatCurrency({ value: cleared })}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency({ value: Math.max(0, invoice.amountDue - cleared) })}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Button asChild variant="ghost" size="sm">
                            <Link
                              to={buildInventoryWorkspacePath({
                                inventoryId: invoice.inventoryId,
                                salesOrderId: invoice.soId,
                                invoiceId: invoice.id,
                                focus: "sell-financials",
                              })}
                            >
                              Workspace
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {invoicesQuery.hasNextPage ? (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => invoicesQuery.fetchNextPage()}
                    loading={invoicesQuery.isFetchingNextPage}
                    loadingText="Loading..."
                  >
                    Load more
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Record Invoice Payment</CardTitle>
              <CardDescription>Record cleared invoice payments.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <Select value={invoicePaymentId} onValueChange={setInvoicePaymentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {invoices.map((invoice) => (
                    <SelectItem key={invoice.id} value={invoice.id}>
                      {invoice.id.slice(0, 8)} · {formatCurrency({ value: invoice.amountDue })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={invoicePaymentAmount}
                onChange={(event) => setInvoicePaymentAmount(event.target.value)}
                placeholder="Payment amount"
              />
              <Button
                onClick={handleRecordInvoicePayment}
                disabled={!invoicePaymentId || !invoicePaymentAmount}
                loading={recordPaymentMutation.isPending}
                loadingText="Recording..."
              >
                Record Payment
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payables" className="mt-0 space-y-6">
          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Purchase Order Payables</CardTitle>
                <CardDescription>Track purchase-order-linked payables and continue in the inventory workspace.</CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={exportPending}>
                    {exportPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {exportPending ? "Exporting..." : "Export"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={selectedPayments.length === 0}
                    onClick={() =>
                      exportPaymentsToCsv(
                        selectedPayments,
                        `payments-selected-${buildExportSuffix(dateRange)}.csv`,
                      )
                    }
                  >
                    Export Selected Payments CSV ({selectedPayments.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={selectedPayments.length === 0}
                    onClick={() =>
                      exportPaymentsToExcel(
                        selectedPayments,
                        `payments-selected-${buildExportSuffix(dateRange)}.xlsx`,
                      )
                    }
                  >
                    Export Selected Payments Excel ({selectedPayments.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("payments", "csv")}>
                    {dateRange?.from
                      ? "Export Payments CSV — all in date range"
                      : "Export Payments CSV — all"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("payments", "excel")}>
                    {dateRange?.from
                      ? "Export Payments Excel — all in date range"
                      : "Export Payments Excel — all"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("payables", "csv")}>
                    {dateRange?.from
                      ? "Export Payables CSV — all in date range"
                      : "Export Payables CSV — all"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => runExport("payables", "excel")}>
                    {dateRange?.from
                      ? "Export Payables Excel — all in date range"
                      : "Export Payables Excel — all"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:w-1/2">
                <div className="space-y-1">
                  <Label>PO Status</Label>
                  <Select
                    value={poStatusFilter}
                    onValueChange={(value) => setPoStatusFilter(value as POStatus | "all")}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="voided">Voided (Bought Back)</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mb-4 text-sm text-muted-foreground">
                Showing {filteredSortedPayables.length.toLocaleString()} payable
                {filteredSortedPayables.length === 1 ? "" : "s"}
                {purchaseOrdersQuery.hasNextPage ? " (more available)" : ""} ·{" "}
                {formatDateRangeLabel(dateRange)}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label="Select all payables"
                        checked={
                          allPayablesSelected
                            ? true
                            : somePayablesSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={(checked) =>
                          toggleAllPayableSelection(checked === true)
                        }
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableHead>
                    <TableHead>Inventory</TableHead>
                    <TableHead>Purchase Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Buy Price</TableHead>
                    <TableHead className="text-right">Cleared</TableHead>
                    <TableHead className="text-right">
                      <PayablesSortButton
                        label="Outstanding"
                        sortKey="outstanding"
                        sortState={payablesSortState}
                        onSort={handlePayablesSort}
                      />
                    </TableHead>
                    <TableHead>
                      <PayablesSortButton
                        label="Next Payment Date"
                        sortKey="nextPaymentDate"
                        sortState={payablesSortState}
                        onSort={handlePayablesSort}
                      />
                    </TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSortedPayables.map((purchaseOrder) => {
                    const cleared = clearedByPoId.get(purchaseOrder.id) || 0;
                    const total = getPurchaseOrderTotal({
                      buyPrice: purchaseOrder.buyPrice,
                      taxCost: purchaseOrder.taxCost,
                      totalAmount: purchaseOrder.totalAmount,
                    });
                    return (
                      <TableRow
                        key={purchaseOrder.id}
                        className="cursor-pointer"
                        onClick={() =>
                          openFinanceSheet({
                            type: "po",
                            id: purchaseOrder.id,
                            tab: "payables",
                          })
                        }
                      >
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            aria-label={`Select payable ${purchaseOrder.id.slice(0, 8)}`}
                            checked={selectedPayablePurchaseOrderIds.has(purchaseOrder.id)}
                            onCheckedChange={(checked) =>
                              togglePayableSelection(purchaseOrder.id, checked === true)
                            }
                          />
                        </TableCell>
                        <TableCell>{purchaseOrder.inventoryId?.slice(0, 8) ?? "—"}</TableCell>
                        <TableCell>
                          {purchaseOrder.vendorDisplayName || purchaseOrder.vendorEmail || purchaseOrder.id.slice(0, 8)}
                        </TableCell>
                        <TableCell>{formatStatusLabel(getPurchaseOrderDisplayStatus(purchaseOrder))}</TableCell>
                        <TableCell className="text-right">{formatCurrency({ value: total })}</TableCell>
                        <TableCell className="text-right">{formatCurrency({ value: cleared })}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency({ value: Math.max(0, total - cleared) })}
                        </TableCell>
                        <TableCell>
                          {purchaseOrder.nextPaymentDate
                            ? formatTimestamp(purchaseOrder.nextPaymentDate, "yyyy-MM-dd")
                            : "-"}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Button asChild variant="ghost" size="sm">
                            <Link
                              to={buildInventoryWorkspacePath({
                                inventoryId: purchaseOrder.inventoryId,
                                purchaseOrderId: purchaseOrder.id,
                                focus: "purchase-financials",
                              })}
                            >
                              Workspace
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {purchaseOrdersQuery.hasNextPage ? (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => purchaseOrdersQuery.fetchNextPage()}
                    loading={purchaseOrdersQuery.isFetchingNextPage}
                    loadingText="Loading..."
                  >
                    Load more
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Record PO Payment</CardTitle>
              <CardDescription>Record cleared payments against purchase orders.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={poPaymentId} onValueChange={setPoPaymentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select purchase order" />
                </SelectTrigger>
                <SelectContent>
                  {purchaseOrders.map((purchaseOrder) => (
                    <SelectItem key={purchaseOrder.id} value={purchaseOrder.id}>
                      {purchaseOrder.vendorDisplayName || purchaseOrder.vendorEmail || purchaseOrder.id.slice(0, 8)} · {formatCurrency({
                        value: getPurchaseOrderTotal({
                          buyPrice: purchaseOrder.buyPrice,
                          taxCost: purchaseOrder.taxCost,
                          totalAmount: purchaseOrder.totalAmount,
                        }),
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {poPaymentId ? (
                <RecordPaymentForm
                  appliesToType="po"
                  appliesToId={poPaymentId}
                  defaultMethod={purchaseOrders.find((po) => po.id === poPaymentId)?.paymentMethod ?? undefined}
                  onSuccess={() => setPoPaymentId("")}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Select a purchase order to record a payment.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Scheduled Payments
                {scheduledPayments.length > 0 && (
                  <Badge>{scheduledPayments.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Upcoming PO payments pending clearance, sorted by scheduled date.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {scheduledPayments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No scheduled payments.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>PO</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Scheduled Date</TableHead>
                      <TableHead className="w-[140px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduledPayments.map((payment) => {
                      const linkedPo = purchaseOrders.find((po) => po.id === payment.appliesToId);
                      return (
                        <TableRow key={payment.id}>
                          <TableCell>
                            {linkedPo?.vendorDisplayName || linkedPo?.vendorEmail || "—"}
                          </TableCell>
                          <TableCell>
                            <button
                              className="font-mono text-xs underline underline-offset-2"
                              onClick={() =>
                                openFinanceSheet({ type: "po", id: payment.appliesToId, tab: "payables" })
                              }
                            >
                              {payment.appliesToId.slice(0, 8)}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency({ value: payment.amount })}
                          </TableCell>
                          <TableCell>{formatStatusLabel(payment.method)}</TableCell>
                          <TableCell>
                            {formatTimestamp(payment.paidAt || payment.createdAt, "yyyy-MM-dd") || "—"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={markClearedMutation.isPending}
                              loading={
                                markClearedMutation.isPending &&
                                markClearedMutation.variables?.paymentId ===
                                  payment.id
                              }
                              loadingText="Clearing..."
                              onClick={() =>
                                markClearedMutation.mutate({
                                  paymentId: payment.id,
                                  status: "cleared",
                                })
                              }
                            >
                              Mark Cleared
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <InvoiceSheet
        invoiceId={selectedInvoiceId}
        open={activeSheetType === "invoice"}
        onOpenChange={(open) => {
          if (!open) {
            closeFinanceSheet("invoice");
          }
        }}
      />

      <SalesOrderSheet
        soId={selectedSoId}
        open={activeSheetType === "so"}
        onOpenChange={(open) => {
          if (!open) {
            closeFinanceSheet("so");
          }
        }}
      />

      <PurchaseOrderSheet
        poId={selectedPoId}
        open={activeSheetType === "po"}
        onOpenChange={(open) => {
          if (!open) {
            closeFinanceSheet("po");
          }
        }}
      />
    </div>
  );
}
